import { useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsChevronRight, BsChevronDown } from 'react-icons/bs';

import Checkbox from '#globalComponents/Checkbox/Checkbox';

/**
 * Recursively count descendants that are in selectedSet (for indeterminate state).
 */
const countSelectedDescendants = (code, childrenByParent, selectedSet) => {
	const children = childrenByParent.get(code) || [];
	let count = 0;
	for (const child of children) {
		if (selectedSet.has(child.code)) count++;
		count += countSelectedDescendants(child.code, childrenByParent, selectedSet);
	}
	return count;
};

/**
 * Recursively count all descendants of a node.
 */
const countAllDescendants = (code, childrenByParent) => {
	const children = childrenByParent.get(code) || [];
	let count = children.length;
	for (const child of children) {
		count += countAllDescendants(child.code, childrenByParent);
	}
	return count;
};

export const WzCodeTreeNode = ({
	node,
	childrenByParent,
	selectedSet,
	expandedSet,
	onToggleCheck,
	onToggleExpand,
	depth = 0,
}) => {
	const children = childrenByParent.get(node.code) || [];
	const hasChildren = children.length > 0;
	const isExpanded = expandedSet.has(node.code);
	const isChecked = selectedSet.has(node.code);

	const selectedDescendants = useMemo(
		() => (hasChildren ? countSelectedDescendants(node.code, childrenByParent, selectedSet) : 0),
		[node.code, childrenByParent, selectedSet, hasChildren],
	);
	const totalDescendants = useMemo(
		() => (hasChildren ? countAllDescendants(node.code, childrenByParent) : 0),
		[node.code, childrenByParent, hasChildren],
	);
	const isIndeterminate = !isChecked && selectedDescendants > 0;

	const isSection = node.level === 1;
	const paddingLeft = depth * 16;

	const rowBgClass = isSection
		? 'bg-gradient-to-r from-purple-50 to-pink-50 font-semibold border-l-2 border-purple-400'
		: isChecked
			? 'bg-purple-50/70 border-l-2 border-purple-500'
			: isIndeterminate
				? 'bg-purple-50/30 border-l-2 border-purple-300'
				: 'border-l-2 border-transparent hover:bg-purple-50/40 hover:border-purple-200';

	return (
		<div>
			<div
				className={`flex items-center gap-1 py-1 px-2 transition-colors ${rowBgClass}`}
				style={{ paddingLeft: `${paddingLeft + 8}px` }}
			>
				{hasChildren ? (
					<button
						type="button"
						onClick={() => onToggleExpand(node.code)}
						className="p-0.5 hover:bg-purple-100 rounded flex-shrink-0 text-bgBase-800 hover:text-primary-700 transition-colors"
						aria-label={isExpanded ? 'Einklappen' : 'Aufklappen'}
					>
						{isExpanded ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
					</button>
				) : (
					<span className="w-4 flex-shrink-0" />
				)}

				<Checkbox
					id={`wz-${node.code}`}
					checked={isChecked}
					showDash={isIndeterminate}
					onChange={(checked) => onToggleCheck(node, checked)}
				/>

				<button
					type="button"
					onClick={() => onToggleCheck(node, !isChecked)}
					className="flex-1 min-w-0 text-left flex items-baseline gap-2 cursor-pointer"
				>
					<span
						className={`font-mono text-[11px] flex-shrink-0 ${
							isSection ? 'text-primary-700' : isChecked ? 'text-primary-700' : 'text-bgBase-800'
						}`}
					>
						{node.code}
					</span>
					<span
						className={`text-xs truncate ${
							isSection
								? 'text-primary-700 uppercase tracking-wide'
								: isChecked
									? 'text-primary-700 font-medium'
									: 'text-text'
						}`}
					>
						{node.name}
					</span>
					{hasChildren && (
						<span
							className={`text-[10px] flex-shrink-0 ml-auto ${
								selectedDescendants > 0 ? 'text-primary-600 font-semibold' : 'text-bgBase-800'
							}`}
						>
							{selectedDescendants > 0 ? `${selectedDescendants}/${totalDescendants}` : totalDescendants}
						</span>
					)}
				</button>
			</div>

			{isExpanded && hasChildren && (
				<div>
					{children.map((child) => (
						<WzCodeTreeNode
							key={child.code}
							node={child}
							childrenByParent={childrenByParent}
							selectedSet={selectedSet}
							expandedSet={expandedSet}
							onToggleCheck={onToggleCheck}
							onToggleExpand={onToggleExpand}
							depth={depth + 1}
						/>
					))}
				</div>
			)}
		</div>
	);
};

WzCodeTreeNode.propTypes = {
	node: PropTypes.shape({
		code: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		level: PropTypes.number.isRequired,
	}).isRequired,
	childrenByParent: PropTypes.instanceOf(Map).isRequired,
	selectedSet: PropTypes.instanceOf(Set).isRequired,
	expandedSet: PropTypes.instanceOf(Set).isRequired,
	onToggleCheck: PropTypes.func.isRequired,
	onToggleExpand: PropTypes.func.isRequired,
	depth: PropTypes.number,
};

export default WzCodeTreeNode;
